import { Link } from "react-router-dom";
import { t } from "../i18n/t";

/**
 * §B1.4's `<StageNav>`, as a props-only primitive, restyled as the redesign's stage rail (guide §3): equal
 * columns, each step a link with a 3 px top border, a Geist Mono first line (`01 · done`, `02 · current`,
 * `05`, `04 · stopped`) and the label below. The state → step computation lives in `src/screens/stageRoute.ts`
 * (task 06b); this renders whatever it is handed, so that the connect, scope, review, dry-run and status
 * screens (tasks 07, 08, 09) render the same nav without importing one another.
 */
export type StageStep = {
  id: string;
  label: string;
  href: string | null;
  state: "done" | "current" | "upcoming" | "stopped";
  /** Replaces the word "current" on the active step ("02 · extracting", "02 · paused"). */
  note?: string;
};

function indexLabel(index: number, state: StageStep["state"], note?: string): string {
  const number = String(index + 1).padStart(2, "0");
  if (state === "done") return `${number} · ${t("stage.done")}`;
  if (state === "current") return `${number} · ${note ?? t("stage.current")}`;
  if (state === "stopped") return `${number} · ${t("stage.stopped")}`;
  return number;
}

export function StageNav({ steps }: { steps: readonly StageStep[] }): JSX.Element {
  return (
    <nav className="stagenav" aria-label={t("stage.nav_label")}>
      <ol className={`stagenav__list stagenav__list--${steps.length === 4 ? "4" : "5"}`}>
        {steps.map((step, index) => {
          const className = `stagenav__step stagenav__step--${step.state}`;
          const ariaCurrent =
            step.state === "current" || step.state === "stopped" ? ("step" as const) : undefined;
          const content = (
            <>
              <span className="stagenav__index">{indexLabel(index, step.state, step.note)}</span>
              {step.label}
            </>
          );
          return (
            <li key={step.id} className="stagenav__item">
              {step.href === null ? (
                <span className={className} aria-current={ariaCurrent}>
                  {content}
                </span>
              ) : (
                <Link className={className} to={step.href} aria-current={ariaCurrent}>
                  {content}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
